import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FirmProfile,
  inviteUser,
  listFirms,
  parseTokenClaims,
  upsertFirm,
  type UserRole,
} from "../api";
import { SmsTab, SmtpTab, WhatsAppTab } from "./SettingsPage";

type DetailTab = "profile" | "owner" | "smtp" | "sms" | "whatsapp";

const PROFILE_FIELDS: { key: keyof FirmProfile; labelKey: string; placeholder?: string }[] = [
  { key: "legal_name", labelKey: "settings.company.legalName" },
  { key: "tax_number", labelKey: "settings.company.taxNumber" },
  { key: "tax_office", labelKey: "settings.company.taxOffice" },
  { key: "address", labelKey: "settings.company.address" },
  { key: "city", labelKey: "settings.company.city" },
  { key: "phone", labelKey: "settings.company.phone" },
  { key: "email", labelKey: "settings.company.email" },
  { key: "kvkk_contact", labelKey: "settings.company.kvkkContact" },
  { key: "logo_url", labelKey: "settings.company.logoUrl", placeholder: "https://..." },
];

export function FirmsPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const isSuper = parseTokenClaims()?.role === "SUPERADMIN";
  const [firms, setFirms] = useState<FirmProfile[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  function reload() {
    setLoading(true);
    listFirms()
      .then(setFirms)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (isSuper) reload();
    else setLoading(false);
  }, [isSuper]);

  if (!isSuper) {
    return (
      <div className="page">
        <h2>{t("firms.title")}</h2>
        <p className="error">{t("firms.superOnly")}</p>
      </div>
    );
  }

  const selectedFirm = firms.find((f) => f.id === selected) ?? null;

  return (
    <div className="page">
      <h2>{t("firms.title")}</h2>
      <p className="muted">{t("firms.intro")}</p>
      {error && <p className="error">{error}</p>}

      <div className="firms-layout">
        <div className="firms-list">
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setCreating(true);
              setSelected(null);
            }}
          >
            + {t("firms.new")}
          </button>
          {loading && <p>{t("common:actions.loading")}</p>}
          <ul className="firms-ul">
            {firms.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  className={`firms-item ${selected === f.id && !creating ? "active" : ""}`}
                  onClick={() => {
                    setSelected(f.id);
                    setCreating(false);
                  }}
                >
                  <strong>{f.name}</strong>
                  <span className="muted small">
                    {f.id}
                    {f.tax_number ? ` · ${f.tax_number}` : ""}
                    {f.active ? "" : ` · ${t("firms.inactive")}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="firms-detail">
          {creating && (
            <FirmForm
              key="new"
              mode="create"
              onSaved={(f) => {
                reload();
                setCreating(false);
                setSelected(f.id);
              }}
            />
          )}
          {!creating && selectedFirm && (
            <FirmDetail key={selectedFirm.id} firm={selectedFirm} onSaved={reload} />
          )}
          {!creating && !selectedFirm && (
            <p className="muted">{t("firms.selectHint")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function FirmDetail({ firm, onSaved }: { firm: FirmProfile; onSaved: () => void }) {
  const { t } = useTranslation(["dashboard", "common"]);
  const [tab, setTab] = useState<DetailTab>("profile");
  const detailTabs: { id: DetailTab; labelKey: string }[] = [
    { id: "profile", labelKey: "firms.tabs.profile" },
    { id: "owner", labelKey: "firms.tabs.owner" },
    { id: "smtp", labelKey: "settings.tabs.smtp" },
    { id: "sms", labelKey: "settings.tabs.sms" },
    { id: "whatsapp", labelKey: "settings.tabs.whatsapp" },
  ];

  return (
    <div>
      <h3>{firm.name}</h3>
      <div className="tabs">
        {detailTabs.map((dt) => (
          <button
            key={dt.id}
            type="button"
            className={`tab ${tab === dt.id ? "active" : ""}`}
            onClick={() => setTab(dt.id)}
          >
            {t(dt.labelKey)}
          </button>
        ))}
      </div>
      <div className="tab-content">
        {tab === "profile" && <FirmForm mode="edit" firm={firm} onSaved={onSaved} />}
        {tab === "owner" && <OwnerInviteForm firm={firm} />}
        {tab === "smtp" && <SmtpTab firmId={firm.id} />}
        {tab === "sms" && <SmsTab firmId={firm.id} />}
        {tab === "whatsapp" && <WhatsAppTab firmId={firm.id} />}
      </div>
    </div>
  );
}

function FirmForm({
  mode,
  firm,
  onSaved,
}: {
  mode: "create" | "edit";
  firm?: FirmProfile;
  onSaved: (f: FirmProfile) => void;
}) {
  const { t } = useTranslation(["dashboard", "common"]);
  const [form, setForm] = useState<Partial<FirmProfile> & { id: string; name: string }>(
    firm
      ? { ...firm }
      : { id: "", name: "", active: true },
  );
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function update<K extends keyof FirmProfile>(key: K, value: FirmProfile[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!form.id.trim() || !form.name.trim()) {
      setError(t("firms.idNameRequired"));
      return;
    }
    try {
      const saved = await upsertFirm(form);
      setInfo(t("settings.saved"));
      onSaved(saved);
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <form className="settings-form" onSubmit={onSubmit}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <label>
        {t("firms.fields.id")}
        <input
          value={form.id}
          onChange={(e) => update("id", e.target.value)}
          disabled={mode === "edit"}
          placeholder="firm-acme"
        />
      </label>
      <label>
        {t("settings.company.name")}
        <input value={form.name} onChange={(e) => update("name", e.target.value)} required />
      </label>
      {PROFILE_FIELDS.map((pf) => (
        <label key={pf.key}>
          {t(pf.labelKey)}
          <input
            value={(form[pf.key] as string | null | undefined) ?? ""}
            onChange={(e) => update(pf.key, e.target.value as never)}
            placeholder={pf.placeholder}
          />
        </label>
      ))}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.active ?? true}
          onChange={(e) => update("active", e.target.checked)}
        />
        {t("firms.fields.active")}
      </label>
      <button type="submit">{t("common:actions.save")}</button>
    </form>
  );
}

// First fleet-owner assignment: the platform operator creates the firm's first
// ADMIN (the fleet owner) and the system e-mails them a single-use link to set
// their own password. `platform: true` is implicit in inviteUser via the gateway
// firm_id override — but here we explicitly pass firm.id and rely on the body
// carrying it (SUPERADMIN in any switcher state writes to this firm because the
// owner form always targets the firm shown in the detail pane).
function OwnerInviteForm({ firm }: { firm: FirmProfile }) {
  const { t } = useTranslation(["dashboard", "common"]);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("ADMIN");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!username.trim() || !email.trim()) {
      setError(t("firms.owner.required"));
      return;
    }
    try {
      const created = await inviteUser(
        {
          firm_id: firm.id,
          username: username.trim(),
          display_name: displayName || undefined,
          email: email.trim(),
          role,
        },
        // Always target this firm, not the SUPERADMIN's active switcher firm.
        { platform: true },
      );
      if (created.invitation_email_sent === false) {
        setInfo(t("users.inviteEmailFailed"));
      } else {
        setInfo(t("users.inviteEmailSent", { email: email.trim() }));
      }
      setUsername("");
      setDisplayName("");
      setEmail("");
      setRole("ADMIN");
    } catch (err) {
      setError(String(err));
    }
  }

  return (
    <form className="settings-form" onSubmit={onSubmit}>
      <p className="muted">{t("firms.owner.intro")}</p>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <label>
        {t("users.usernamePlaceholder")}
        <input value={username} onChange={(e) => setUsername(e.target.value)} required />
      </label>
      <label>
        {t("users.displayNamePlaceholder")}
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </label>
      <label>
        {t("users.emailPlaceholder")}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label>
        {t("users.th.role")}
        <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
          <option value="ADMIN">ADMIN</option>
          <option value="OPERATOR">OPERATOR</option>
        </select>
      </label>
      <button type="submit">{t("firms.owner.invite")}</button>
    </form>
  );
}
